/**
 * Busca todas as páginas de um endpoint paginado do padrão `{ detail, data }`.
 *
 * Existe porque três telas precisavam da base completa (indicadores de
 * credenciamento, capacidade por polo, limites de polo) e cada cópia do laço de
 * paginação divergia da outra — só uma expunha o truncamento, e todas
 * descartavam em silêncio uma página que voltasse sem `data`.
 *
 * Garantias:
 * - Página com erro descrito, `Error` de rede ou **sem `data`** interrompe a
 *   busca com exceção. Nunca some linha sem avisar (`request` devolve
 *   `data: undefined` para corpo vazio, o que antes era ignorado).
 * - `truncated` diz se o backend reportou mais páginas do que o teto permite,
 *   para a tela poder ser honesta sobre estar mostrando dado parcial.
 * - Concorrência limitada: 40 páginas não viram 40 requisições simultâneas.
 */

interface PagedEnvelope<T> {
  detail: {
    description?: string;
    total_pages?: number;
  };
  data?: T[];
}

interface FetchAllPagesOptions {
  /** Teto de páginas buscadas. Excedido, `truncated` volta `true`. */
  maxPages?: number;
  /** Requisições simultâneas por lote. */
  concurrency?: number;
}

export interface PagedResult<T> {
  rows: T[];
  truncated: boolean;
  /** Total de páginas informado pelo backend, antes de aplicar o teto. */
  reportedPages: number;
}

const GENERIC_ERROR = 'Algo deu errado, tente novamente.';

export async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<PagedEnvelope<T> | Error>,
  { maxPages = 20, concurrency = 6 }: FetchAllPagesOptions = {}
): Promise<PagedResult<T>> {
  const unwrap = (response: PagedEnvelope<T> | Error): T[] => {
    if (response instanceof Error) throw response;
    if (response.detail.description) {
      throw new Error(response.detail.description);
    }
    if (!response.data) throw new Error(GENERIC_ERROR);
    return response.data;
  };

  const first = await fetchPage(1);
  const rows = [...unwrap(first)];
  const reportedPages =
    (first instanceof Error ? 1 : first.detail.total_pages) ?? 1;
  const lastPage = Math.min(reportedPages, maxPages);

  for (let page = 2; page <= lastPage; page += concurrency) {
    const batch = [];
    for (
      let offset = 0;
      offset < concurrency && page + offset <= lastPage;
      offset += 1
    ) {
      batch.push(fetchPage(page + offset));
    }
    // Lotes são sequenciais de propósito: limita a concorrência.
    // eslint-disable-next-line no-await-in-loop
    const responses = await Promise.all(batch);
    responses.forEach((response) => rows.push(...unwrap(response)));
  }

  return { rows, truncated: reportedPages > maxPages, reportedPages };
}
